import React from 'react';
import { FaUsers, FaChartLine, FaHeartbeat, FaGraduationCap, FaArrowUp, FaArrowDown, FaMapMarkerAlt } from 'react-icons/fa';

function Bomet() {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* County Header */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <div className="flex items-center mb-4">
            <FaMapMarkerAlt className="text-4xl text-indigo-600 mr-4" />
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Bomet County</h1>
              <p className="text-gray-500 mt-1">South Rift Region · County No. 36</p>
            </div>
          </div>
          <p className="text-gray-600 text-lg">
            Bomet County is located in the South Rift region of Kenya. 
            It borders Kericho, Narok, Nyamira, and Nakuru counties. 
            The county is known for tea farming and is home to the Kipsigis community.
          </p>
        </div>

        {/* Key Metrics - Population */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <FaUsers className="text-2xl text-indigo-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Population</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <MetricCard
              title="Total Population"
              value="1.0M"
              change="+2.1%"
              isPositive={true}
              subtitle="2024 estimate"
            />
            <MetricCard
              title="Growth Rate"
              value="2.1%"
              change="annually"
              isPositive={true}
              subtitle="Per year"
            />
            <MetricCard
              title="Urban Population"
              value="12.3%"
              change="+0.8%"
              isPositive={true}
              subtitle="Of total"
            />
            <MetricCard
              title="Median Age"
              value="18.2"
              change="years"
              isPositive={null}
              subtitle="2024"
            />
          </div>
        </div>

        {/* Key Metrics - Economy */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <FaChartLine className="text-2xl text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Economy</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <MetricCard
              title="GDP Contribution"
              value="0.8%"
              change="+0.1%"
              isPositive={true}
              subtitle="National GDP"
            />
            <MetricCard
              title="Main Industry"
              value="Tea"
              change="Farming"
              isPositive={null}
              subtitle="Primary sector"
            />
            <MetricCard
              title="Unemployment"
              value="7.2%"
              change="-0.3%"
              isPositive={true}
              subtitle="2024"
            />
            <MetricCard
              title="Poverty Rate"
              value="43.2%"
              change="-2.1%"
              isPositive={true}
              subtitle="Below poverty line"
            />
          </div>
        </div>

        {/* Key Metrics - Health */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <FaHeartbeat className="text-2xl text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Health</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <MetricCard
              title="Life Expectancy"
              value="64.3 yrs"
              change="+0.8"
              isPositive={true}
              subtitle="2024"
            />
            <MetricCard
              title="Infant Mortality"
              value="38.2"
              change="-1.4"
              isPositive={true}
              subtitle="per 1,000 births"
            />
            <MetricCard
              title="Health Facilities"
              value="186"
              change="+12"
              isPositive={true}
              subtitle="Facilities"
            />
            <MetricCard
              title="Immunization"
              value="78.4%"
              change="+2.3%"
              isPositive={true}
              subtitle="Coverage"
            />
          </div>
        </div>

        {/* Key Metrics - Education */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <FaGraduationCap className="text-2xl text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">Education</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            <MetricCard
              title="Literacy Rate"
              value="72.4%"
              change="+1.8%"
              isPositive={true}
              subtitle="Adults 15+"
            />
            <MetricCard
              title="Primary Enrollment"
              value="94.2%"
              change="+1.2%"
              isPositive={true}
              subtitle="Gross rate"
            />
            <MetricCard
              title="Secondary Enrollment"
              value="52.3%"
              change="+3.1%"
              isPositive={true}
              subtitle="Net rate"
            />
            <MetricCard
              title="Primary Schools"
              value="612"
              change="+18"
              isPositive={true}
              subtitle="Schools"
            />
          </div>
        </div>

        {/* Data Tables */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Sub Counties */}
          <DataSection
            title="Sub Counties"
            data={[
              { label: 'Bomet Central', value: 'HQ', count: 'Bomet Town' },
              { label: 'Bomet East', value: 'Merigi', count: '' },
              { label: 'Chepalungu', value: 'Sigor', count: '' },
              { label: 'Konoin', value: 'Kimulot', count: '' },
              { label: 'Sotik', value: 'Sotik', count: '' },
            ]}
          />

          {/* Key Facts */}
          <DataSection
            title="Key Facts"
            data={[
              { label: 'Area', value: '1,997', count: 'km²' },
              { label: 'County Headquarters', value: 'Bomet', count: 'Town' },
              { label: 'Constituencies', value: '5', count: '' },
              { label: 'Wards', value: '25', count: '' },
              { label: 'Main Crops', value: 'Tea, Maize', count: 'Milk' },
            ]}
          />
        </div>

        {/* Data Source */}
        <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
          <p className="text-sm text-gray-700">
            <strong>Data Sources:</strong> Kenya National Bureau of Statistics (KNBS), 
            Bomet County Government, Kenya Demographic and Health Survey (KDHS) 2024
          </p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ title, value, change, isPositive, subtitle }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <p className="text-gray-600 text-sm mb-2">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mb-2">{value}</p>
      <div className="flex items-center">
        {isPositive !== null && (
          isPositive ?
            <FaArrowUp className="text-green-600 mr-1" /> :
            <FaArrowDown className="text-red-600 mr-1" />
        )}
        <p className={`text-sm ${isPositive === true ? 'text-green-600' : isPositive === false ? 'text-red-600' : 'text-gray-600'}`}>
          {change}
        </p>
      </div>
      <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
    </div>
  );
}

function DataSection({ title, data }) {
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="flex justify-between items-center border-b pb-2">
            <span className="text-gray-700">{item.label}</span>
            <div className="text-right">
              <span className="font-semibold text-gray-900">{item.value}</span>
              <span className="text-sm text-gray-500 ml-2">{item.count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Bomet;